import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { navigateWithThreadPreservation } from '@/utils/thread-navigation'
import { PaneListEmptyState } from '@/components/layout/pane-list-empty-state'
import { PaneListError, PaneListLoading, PaneListSection } from '@/components/layout/pane-list-section'
import { PaneResponsiveCardGrid } from '@/components/layout/pane-responsive-card-grid'
import { OrganisationCard } from '@/components/organisation-card'
import { Button } from '@/components/ui/button'
import { Building2, Plus } from 'lucide-react'
import { useOrganisations, Organisation } from '../hooks/use-organisations'
import { cn } from '@/lib/utils'

interface OrganisationListProps {
  /** Opens the employer create-org flow (wired from the page alongside PageHeader actions). */
  onOpenCreate?: () => void
}

export function OrganisationList({ onOpenCreate }: OrganisationListProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { organisations, isLoading, error } = useOrganisations()

  const [highlightedOrganisations, setHighlightedOrganisations] = useState<Set<string>>(new Set())
  const previousOrganisationsRef = useRef<Organisation[]>([])

  useEffect(() => {
    if (organisations && previousOrganisationsRef.current.length > 0) {
      const changedIds = new Set<string>()

      organisations.forEach((org) => {
        const prev = previousOrganisationsRef.current.find((o) => o.id === org.id)
        if (!prev) {
          changedIds.add(org.id)
          console.log('[ORGANISATIONS] New organisation detected:', org.id, org.name)
        } else {
          if (JSON.stringify(prev) !== JSON.stringify(org)) {
            changedIds.add(org.id)
            console.log('[ORGANISATIONS] Updated organisation detected:', org.id, org.name)
          }
        }
      })

      if (changedIds.size > 0) {
        console.log('[ORGANISATIONS] Highlighting organisations:', Array.from(changedIds))
        setHighlightedOrganisations(changedIds)
        setTimeout(() => {
          setHighlightedOrganisations(new Set())
        }, 3000)
      }
    }

    if (organisations) {
      previousOrganisationsRef.current = organisations
    }
  }, [organisations])

  const handleOpenOrg = (orgSlug: string) => {
    navigateWithThreadPreservation(navigate, `/employer/organisation/${orgSlug}`, searchParams)
  }

  if (isLoading) {
    return (
      <PaneListSection title="Organisations" showHeading={false}>
        <PaneListLoading message="Loading organisations…" />
      </PaneListSection>
    )
  }

  if (error) {
    return (
      <PaneListSection title="Organisations" showHeading={false}>
        <PaneListError message="Failed to load organisations" />
      </PaneListSection>
    )
  }

  return (
    <PaneListSection title="Organisations" showHeading={false}>
      {organisations.length === 0 ? (
        <PaneListEmptyState
          icon={<Building2 aria-hidden />}
          title="No organisations yet"
          description="Create your first organisation to start posting opportunities"
          action={
            <Button type="button" variant="outline" onClick={() => onOpenCreate?.()}>
              <Plus className="mr-2 h-4 w-4" />
              Create Organisation
            </Button>
          }
        />
      ) : (
        <PaneResponsiveCardGrid>
          {organisations.map((org) => (
            <OrganisationCard
              key={org.id}
              name={org.name}
              slug={org.slug}
              description={org.description}
              website={org.website}
              industry={org.industry}
              logoUrl={org.logo_url}
              onOpen={() => handleOpenOrg(org.slug)}
              className={cn(
                'transition-all duration-300',
                highlightedOrganisations.has(org.id) &&
                  'ring-2 ring-primary ring-offset-2 animate-pulse',
              )}
            />
          ))}
        </PaneResponsiveCardGrid>
      )}
    </PaneListSection>
  )
}
